import type { UseChatHelpers } from '@ai-sdk/react';
import { useLayoutSyncedRef } from '@talos/kyoko';
import { useCallback, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import type { AvaAIUIMessage } from './AvaAIContext';

type UseAvaAIPowerSearchChatTurnParams = {
  messages: AvaAIUIMessage[];
  status: UseChatHelpers<AvaAIUIMessage>['status'];
  sendChatMessage: UseChatHelpers<AvaAIUIMessage>['sendMessage'];
  setMessages: UseChatHelpers<AvaAIUIMessage>['setMessages'];
  startNewTurn: () => void;
  stop: () => Promise<void>;
};

/**
 * Owns the power-search slice of the shared Ava AI chat session.
 *
 * Sends unmatched search queries as user messages, tracks the active turn by message ID, dedupes
 * repeat sends, and cancels or rolls back that turn when the modal query changes or the AI group
 * unmounts. Call `preserveActiveTurn` before opening the full chat so the continued
 * conversation is not torn down on unmount. Returned callbacks stay stable across streaming updates.
 */
export function useAvaAIPowerSearchChatTurn({
  messages,
  status,
  sendChatMessage,
  setMessages,
  startNewTurn,
  stop,
}: UseAvaAIPowerSearchChatTurnParams) {
  const [powerSearchMessageId, setPowerSearchMessageId] = useState<string | null>(null);
  // Latest chat history for stable send/cancel callbacks without re-running power-search effects on every stream chunk.
  const messagesRef = useLayoutSyncedRef(messages);
  // Latest request status so sendPowerSearchMessage can detect an in-flight turn without depending on status in its deps.
  const statusRef = useLayoutSyncedRef(status);
  // User message ID for the active power-search turn; mirrors powerSearchMessageId for synchronous reads inside callbacks.
  const powerSearchMessageIdRef = useRef<string | null>(null);
  // Last query text sent from power search; used for dedupe before the user message lands in messages.
  const powerSearchTextRef = useRef<string | null>(null);
  // Set when opening chat from a power-search result so unmount cleanup does not cancel the continued turn.
  const preserveActiveTurnRef = useRef(false);

  const cancelPowerSearchTurn = useCallback(() => {
    const messageId = powerSearchMessageIdRef.current;
    powerSearchTextRef.current = null;
    powerSearchMessageIdRef.current = null;
    setPowerSearchMessageId(null);
    if (messageId == null) {
      return;
    }
    const currentMessages = messagesRef.current;
    const index = currentMessages.findIndex(message => message.id === messageId);
    const hasLaterUserMessage = index >= 0 && currentMessages.slice(index + 1).some(message => message.role === 'user');
    if (index < 0 || hasLaterUserMessage) {
      return;
    }
    startNewTurn();
    void stop();
    setMessages(currentMessages.slice(0, index));
  }, [messagesRef, setMessages, startNewTurn, stop]);

  const abandonTurn = useCallback(() => {
    if (preserveActiveTurnRef.current) {
      preserveActiveTurnRef.current = false;
      return;
    }
    cancelPowerSearchTurn();
  }, [cancelPowerSearchTurn]);

  const sendPowerSearchMessage = useCallback(
    async (text: string) => {
      const currentMessages = messagesRef.current;
      const existingId = powerSearchMessageIdRef.current;
      const existingIndex = existingId == null ? -1 : currentMessages.findIndex(message => message.id === existingId);
      const hasLaterUserMessage =
        existingIndex >= 0 && currentMessages.slice(existingIndex + 1).some(message => message.role === 'user');
      const existingText =
        existingIndex >= 0
          ? currentMessages[existingIndex].parts
              .filter(part => part.type === 'text')
              .map(part => part.text)
              .join('')
          : powerSearchTextRef.current;
      const turnInFlight = statusRef.current === 'submitted' || statusRef.current === 'streaming';
      if (existingText === text && (turnInFlight || (existingIndex >= 0 && !hasLaterUserMessage))) {
        return;
      }

      const replacing = existingIndex >= 0 && !hasLaterUserMessage && existingId != null;
      const messageId = replacing ? existingId : uuid();
      powerSearchMessageIdRef.current = messageId;
      setPowerSearchMessageId(messageId);
      powerSearchTextRef.current = text;
      startNewTurn();
      if (replacing) {
        await stop();
        await sendChatMessage({ text, messageId });
        return;
      }
      await sendChatMessage({
        id: messageId,
        role: 'user',
        parts: [{ type: 'text', text }],
      });
    },
    [messagesRef, sendChatMessage, startNewTurn, stop, statusRef]
  );

  const preserveActiveTurn = useCallback(() => {
    preserveActiveTurnRef.current = true;
  }, []);

  return {
    powerSearchMessageId,
    sendPowerSearchMessage,
    cancelPowerSearchTurn,
    abandonTurn,
    preserveActiveTurn,
  };
}
